import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import type { AddressSubValue } from '@checkout-page/schemas';
import Label from '../CheckoutForm/components/Label';
import StyledElement from '../StyledElement';
import {
  createSessionToken,
  fetchPlaceDetails,
  fetchSuggestions,
  loadPlacesLibrary,
  mapPlaceToAddress,
  type Suggestion,
} from './placesApi';

type Props = {
  value: string;
  onChange: (v: string) => void;
  onPickAddress: (partial: Partial<AddressSubValue>) => void;
  country: string;
  label?: string;
  placeholder?: string;
  id?: string;
  required?: boolean;
  className?: string;
  hideLabel?: boolean;
};

const inputClass = clsx('cp_form-textinput');

const DEBOUNCE_MS = 100;

// Google's official "Powered by Google" lockups, served from gstatic. The two
// variants exist because the asset is a single image (text + colored
// wordmark), so we pick the one with adequate contrast against the form's
// input background. Per the Maps Platform branding guidelines we must show
// this attribution when Place Autocomplete predictions render outside a Map.
const POWERED_BY_GOOGLE_LIGHT_BG =
  'https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png';
const POWERED_BY_GOOGLE_DARK_BG =
  'https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-non-white3.png';
const POWERED_BY_GOOGLE_LIGHT_BG_2X =
  'https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3_hdpi.png';
const POWERED_BY_GOOGLE_DARK_BG_2X =
  'https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-non-white3_hdpi.png';

function PoweredByGoogle() {
  const ref = useRef<HTMLDivElement | null>(null);
  // Default to the dark-bg variant (white "Powered by" text). On mount, read
  // the resolved input background color and swap if the background is light.
  const [variant, setVariant] = useState<'light' | 'dark'>('dark');

  useEffect(() => {
    if (!ref.current) return;
    const probe = document.createElement('span');
    probe.style.background = 'var(--cp_checkout-form-fields-input-background-color)';
    probe.style.display = 'none';
    ref.current.appendChild(probe);
    const bg = getComputedStyle(probe).backgroundColor;
    ref.current.removeChild(probe);
    const m = bg.match(/\d+(\.\d+)?/g);
    if (m && m.length >= 3) {
      const [r, g, b] = m.map(Number);
      // ITU-R BT.601 relative luminance
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      setVariant(luminance > 0.6 ? 'light' : 'dark');
    }
  }, []);

  const src1x = variant === 'light' ? POWERED_BY_GOOGLE_LIGHT_BG : POWERED_BY_GOOGLE_DARK_BG;
  const src2x = variant === 'light' ? POWERED_BY_GOOGLE_LIGHT_BG_2X : POWERED_BY_GOOGLE_DARK_BG_2X;
  return (
    <div
      ref={ref}
      className="border-t border-[var(--cp_checkout-form-fields-input-border-color)] px-3 py-2 flex items-center justify-end"
    >
      <img
        src={src1x}
        srcSet={`${src1x} 1x, ${src2x} 2x`}
        alt="Powered by Google"
        height={14}
        className="h-3.5 w-auto"
        loading="lazy"
      />
    </div>
  );
}

export function AutocompleteInput({
  value,
  onChange,
  onPickAddress,
  country,
  label,
  placeholder,
  id,
  required,
  className,
  hideLabel,
}: Props) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [ready, setReady] = useState(false);
  const sessionTokenRef = useRef<any>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const inputId = id ?? 'addr-line1';

  useEffect(() => {
    let cancelled = false;
    loadPlacesLibrary()
      .then(() => {
        if (cancelled) return;
        sessionTokenRef.current = createSessionToken();
        setReady(true);
      })
      .catch(() => {
        // graceful degradation: plain themed input remains
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!value) setSuggestions([]);
  }, [value]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, []);

  const requestSuggestions = (input: string) => {
    if (!ready) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    debounceRef.current = setTimeout(() => {
      void fetchSuggestions({
        input,
        country,
        sessionToken: sessionTokenRef.current,
        signal: controller.signal,
      })
        .then(result => {
          if (controller.signal.aborted) return;
          setSuggestions(result);
        })
        .catch(() => {
          setSuggestions([]);
        });
    }, DEBOUNCE_MS);
  };

  const handleSelect = async (suggestion: Suggestion | null) => {
    if (!suggestion) return;
    try {
      const place = await fetchPlaceDetails({
        placeId: suggestion.placeId,
        sessionToken: sessionTokenRef.current,
      });
      const partial = mapPlaceToAddress(place);
      onPickAddress(partial);
      setSuggestions([]);
      // Per Google billing: one session token covers one "Autocomplete + Details"
      // exchange. Mint a fresh token for the next typing session.
      sessionTokenRef.current = createSessionToken();
    } catch {
      // Clear the stale dropdown so the user can retry without it lingering.
      setSuggestions([]);
    }
  };

  return (
    <Combobox<Suggestion | null> value={null} onChange={handleSelect} immediate>
      <div className="relative">
        {!hideLabel && label && (
          <StyledElement
            as={Label}
            elementPath="checkout.form.fields.label"
            htmlFor={inputId}
            className="cp_form-label"
            required={required}
          >
            {label}
          </StyledElement>
        )}
        <StyledElement
          elementPath="checkout.form.fields.input"
          as={ComboboxInput}
          id={inputId}
          name={inputId}
          aria-label={label ?? placeholder}
          type="text"
          placeholder={placeholder ?? label ?? ''}
          required={required}
          value={value}
          autoComplete="off"
          displayValue={() => value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            onChange(e.target.value);
            // Browser autofill fires input events with no inputType (Chrome)
            // or 'insertReplacementText' (Safari/Firefox); real typing always
            // carries one. Only typing should open the address search —
            // autofill already produced a complete address.
            const { inputType } = e.nativeEvent as Partial<InputEvent>;
            if (inputType && inputType !== 'insertReplacementText') {
              requestSuggestions(e.target.value);
            } else {
              if (debounceRef.current) clearTimeout(debounceRef.current);
              abortRef.current?.abort();
              setSuggestions([]);
            }
          }}
          className={clsx('block w-full appearance-none', className ?? inputClass)}
        />
        {suggestions.length > 0 && (
          <ComboboxOptions
            className={clsx(
              // Side borders overlap the surrounding address-group's 1px border
              // (left/right at -1px) so the dropdown reads as a continuation of
              // the field, not a floating box inset from it.
              'cp-address-autocomplete-options',
              'absolute z-50 top-full mt-1 left-[-1px] right-[-1px] overflow-hidden',
              'border border-[var(--cp_checkout-form-fields-input-border-color)]',
              'bg-[var(--cp_checkout-form-fields-input-background-color)]',
              'text-[var(--cp_checkout-form-fields-input-color)]',
              'shadow-lg focus:outline-none',
            )}
          >
            {/* Scroll inside this wrapper, not on ComboboxOptions itself, so
                the Powered-by-Google attribution sits outside the scroll
                region and stays visible no matter how tall the results are. */}
            <div className="max-h-80 overflow-y-auto">
              {suggestions.map(s => (
                <ComboboxOption
                  key={s.placeId}
                  value={s}
                  className={({ focus }) =>
                    clsx(
                      'cursor-pointer px-3 py-2 text-sm transition-colors',
                      focus &&
                        'bg-[color-mix(in_oklab,var(--cp_checkout-form-fields-input-color)_8%,transparent)]',
                    )
                  }
                >
                  <div>{s.primaryText}</div>
                  {s.secondaryText && (
                    <div className="text-xs opacity-60 mt-0.5">{s.secondaryText}</div>
                  )}
                </ComboboxOption>
              ))}
            </div>
            <PoweredByGoogle />
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  );
}
